import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, GripVertical, Image as ImageIcon, Save, X, Grid3X3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import AdminPageHeader from '../../components/AdminPageHeader';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: '',
    order: 0
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/categories-detailed');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Fehler beim Laden der Kategorien');
    } finally {
      setLoading(false);
    }
  };

  const syncCategoriesFromProducts = async () => {
    try {
      const response = await axiosInstance.post('/admin/categories/sync-from-products');
      toast.success(response.data.message);
      fetchCategories();
    } catch (error) {
      console.error('Error syncing categories:', error);
      toast.error('Fehler beim Synchronisieren der Kategorien');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCategory) {
        await axiosInstance.put(`/admin/categories/${editingCategory.id}`, formData);
        toast.success('Kategorie aktualisiert');
      } else {
        await axiosInstance.post('/admin/categories', formData);
        toast.success('Kategorie erstellt');
      }
      
      setShowAddModal(false);
      setEditingCategory(null);
      setFormData({ name: '', description: '', image: '', order: 0 });
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      image: category.image || '',
      order: category.order
    });
    setShowAddModal(true);
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`Möchten Sie die Kategorie "${category.name}" wirklich löschen?${category.product_count > 0 ? '\n\nACHTUNG: Diese Kategorie enthält ' + category.product_count + ' Produkt(e). Sie müssen zuerst alle Produkte verschieben oder löschen.' : ''}`)) {
      return;
    }

    try {
      await axiosInstance.delete(`/admin/categories/${category.id}`);
      toast.success('Kategorie gelöscht');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/html'));
    
    if (dragIndex === dropIndex) return;

    const newCategories = [...categories];
    const draggedItem = newCategories[dragIndex];
    
    newCategories.splice(dragIndex, 1);
    newCategories.splice(dropIndex, 0, draggedItem);

    // Update order values
    const categoryOrders = newCategories.map((cat, idx) => ({
      id: cat.id,
      order: idx
    }));

    setCategories(newCategories);

    try {
      await axiosInstance.post('/admin/categories/reorder', categoryOrders);
      toast.success('Reihenfolge aktualisiert');
    } catch (error) {
      console.error('Error reordering:', error);
      toast.error('Fehler beim Sortieren');
      fetchCategories(); // Revert on error
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingCategory(null);
    setFormData({ name: '', description: '', image: '', order: 0 });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Lade Kategorien...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 lg:py-8" style={{ backgroundColor: '#F5EDE4', minHeight: '100vh' }}>
      <div className="mb-4 sm:mb-6 lg:mb-8">
        <AdminPageHeader 
          title="Kategorien verwalten" 
          subtitle="Organisieren Sie Ihre Produktkategorien"
          icon={Grid3X3}
        />
        
        {/* Aktionen - Goldbraune Überschrift */}
        <div className="mb-3 p-2 px-3 flex items-center justify-between" style={{ backgroundColor: '#C4A084' }}>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0FFF0]">
              <Plus className="h-4 w-4 text-[#60B060]" />
            </div>
            <h2 className="text-base font-semibold text-white">Aktionen</h2>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2 justify-end mb-4 sm:mb-6 mt-4">
          <Button
            onClick={syncCategoriesFromProducts}
            variant="outline"
            className="w-full sm:w-auto bg-[#F0F8FF] hover:bg-[#E0F0FF] border-[#C4A084] text-[#60A0D0] rounded-none"
          >
            Aus Produkten synchronisieren
          </Button>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-[#C4A084] hover:bg-[#A08060] text-white w-full sm:w-auto rounded-none"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neue Kategorie
          </Button>
        </div>

        {/* Section: Categories List */}
        <h2 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <ImageIcon className="h-4 w-4 text-[#8B7355]" />
          Kategorienliste
        </h2>

        <Card className="rounded-none">
          <CardHeader>
            <CardTitle>Kategorien ({categories.length})</CardTitle>
            <p className="text-xs sm:text-sm text-gray-600">Ziehen Sie die Kategorien, um die Reihenfolge zu ändern</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {categories.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p>Keine Kategorien vorhanden</p>
                  <p className="text-sm mt-2">Klicken Sie auf "Aus Produkten synchronisieren" um Kategorien aus Ihren Produkten zu erstellen</p>
                </div>
              ) : (
                categories.map((category, index) => (
                  <div
                    key={category.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                    className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 border rounded-lg hover:border-[#C4A084] transition-colors bg-white cursor-move overflow-hidden"
                    data-testid={`category-row-${category.slug || category.id}`}
                  >
                    <GripVertical className="w-5 h-5 text-gray-400 shrink-0 hidden sm:block" />
                    
                    {category.image ? (
                      <img
                        src={category.image}
                        alt={category.name}
                        className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded flex items-center justify-center shrink-0">
                        <ImageIcon className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{category.name}</h3>
                      {category.description && (
                        <p className="text-sm text-gray-600 truncate">{category.description}</p>
                      )}
                      <p className="text-sm text-gray-500 mt-1">
                        {category.product_count} Produkt{category.product_count !== 1 ? 'e' : ''}
                      </p>
                    </div>
                    
                    <div className="flex gap-1.5 sm:gap-2 shrink-0">
                      <Button
                        onClick={() => handleEdit(category)}
                        variant="outline"
                        size="sm"
                        className="h-9 w-9 sm:h-10 sm:w-10 p-0"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(category)}
                        variant="outline"
                        size="sm"
                        className="h-9 w-9 sm:h-10 sm:w-10 p-0 text-red-600 hover:bg-red-50"
                        disabled={category.product_count > 0}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Category Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {editingCategory ? 'Kategorie bearbeiten' : 'Neue Kategorie'}
              </h2>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="z.B. Dekoration"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Beschreibung</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optionale Beschreibung der Kategorie"
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="image">Bild URL</Label>
                <Input
                  id="image"
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder="https://..."
                />
                {formData.image && (
                  <img
                    src={formData.image}
                    alt="Preview"
                    className="mt-2 w-full h-32 object-cover rounded"
                  />
                )}
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  type="button"
                  onClick={closeModal}
                  variant="outline"
                  className="flex-1"
                >
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#C4A084] hover:bg-[#B89968] text-white"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Speichern
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;
